import React, { useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import Badge from "../../components/ui-components/Badge";
import { useSetHeaderRecordName } from "../../components/ui-components/HeaderSlot";
import { useUseCaseQuery } from "./hooks/useUseCaseQueries";
import { useCapabilities } from "../../authz/useCapabilities";
import { CAPS } from "../../authz/capabilities";
import OverviewTab from "./tabs/OverviewTab";
import FinancialsTab from "./tabs/FinancialsTab";
import ValueRoiTab from "./tabs/ValueRoiTab";
import UsageTab from "./tabs/UsageTab";
import UseCaseProfileSheet from "./components/UseCaseProfileSheet";

// The app's tab strip lives in the global TopBar and is driven by the pathname
// (getHeaderTabs in headerTabs.js), so a tab must be a path segment. This shell
// reads the trailing segment and renders the matching tab body.
function tabFromPath(pathname) {
  const seg = pathname.split("/")[3] || "";
  switch (seg) {
    case "financials":
    case "value":
    case "usage":
      return seg;
    default:
      return "overview";
  }
}

export default function UseCaseDetailPage() {
  const { nodeId } = useParams();
  const location = useLocation();
  const { data, isLoading, error } = useUseCaseQuery(nodeId);
  const { capabilities } = useCapabilities();
  const canEdit = capabilities.includes(CAPS.CONFIG_DIMENSIONS);
  const [editing, setEditing] = useState(false);
  // Bumped every time the Edit button is clicked so the sheet's `key` changes
  // on open (but not on close), forcing a fresh instance — and therefore
  // fresh useState seeds from the current `data` — each time it opens. This
  // is what stops an abandoned edit (or another use case's edit, after
  // navigating without saving) from leaking into the next open. Closing
  // does not touch this counter, so the key is stable while RightSheet plays
  // its exit animation.
  const [editSession, setEditSession] = useState(0);

  // Feeds the last breadcrumb crumb and the derived page title. Called before
  // the early returns so the hook order stays stable across render states.
  useSetHeaderRecordName(data?.row?.name ?? null);

  if (isLoading) return <div className="p-6">Loading…</div>;
  if (error) return <div className="p-6">Could not load this use case.</div>;
  if (!data) return <div className="p-6">Use case not found.</div>;

  const row = data.row ?? {};
  const tab = tabFromPath(location.pathname);

  return (
    <div className="p-6 flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <div className="text-text-2 text-sm">{(data.path ?? []).join(" · ")}</div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl">{row.name}</h1>
          {row.stage ? <Badge status="neutral" label={row.stage} /> : null}
          {canEdit && (
            <button
              type="button"
              onClick={() => {
                setEditSession((s) => s + 1);
                setEditing(true);
              }}
              className="inline-flex items-center rounded-ctrl border border-solid border-border box-border px-3 py-1 text-sm text-text-2 hover:bg-surface-2 hover:text-text cursor-pointer"
            >
              Edit
            </button>
          )}
        </div>
        <div className="text-text-2 text-sm flex gap-4">
          <span>{row.ownerName || "No owner"}</span>
          {data.startedOn ? <span>Started {data.startedOn}</span> : null}
        </div>
      </header>

      <UseCaseProfileSheet
        key={`${row.nodeId}-${editSession}`}
        open={editing}
        onClose={() => setEditing(false)}
        useCase={data}
      />

      {tab === "overview" && <OverviewTab useCase={data} />}
      {tab === "financials" && <FinancialsTab useCase={data} />}
      {tab === "value" && <ValueRoiTab useCase={data} />}
      {tab === "usage" && <UsageTab useCase={data} />}
    </div>
  );
}
